const ltCopyNormalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
const ltCopySpecification = /^(?:[-•]\s*)?(?:culoare(?:a)?(?: (?:cablu|bec|bulb|glob))?|material(?: (?:bec|bulb|glob))?|forma bulb(?:ului)?|sursa de lumina|cip|voltaj|tensiune(?: de alimentare)?|flux luminos|putere(?: (?:led|glob led|totala ghirlanda luminoasa(?:\s*[\d.,]+\s*m)?))?|unghi de lumina|unghi luminos|cri(?: ra)?|dimabil|grad de protectie(?: exterior)?|protectie|temperatura de culoare|dimensiuni|dimensiunea becului|lungime(?:a)?(?: standard| totala)?|latime|inaltime(?: pendul)?|diametru(?:l)?(?: (?:becului|bulb led|glob(?:ului)?))?|greutate|soclu|fasung|numar(?: de)? (?:becuri|fasunguri|globuri|globuli|bulbi led|(?:mini )?led-?uri(?: incluse)?)|distanta intre (?:becuri|fasunguri|globuri|bulbi|(?:mini )?led-?uri)|frecventa|clasa energetica|durata de viata|garantie|iluminare|lumina glob led|alimentare|panou solar|timp de (?:incarcare|functionare)|baterie reincarcabila|telecomanda inclusa|control (?:prin smartphone|wireless|cu telecomanda))\s*:/i;
const ltCopyAdditionalSpecification = /^(?:material becuri|nr\.? leduri|culoare lumina|durata medie de viata|lumina|panou solar [\d.,]+W\/[\d.,]+V premium - timp de incarcare|un port de incarcare USB\. - timp de incarcare)\s*:/i;
// Observed catalog labels, including dimension/cutting and connected-device variants.
// Keep this anchored to field names: narrative paragraphs are not specifications.
const ltCopyCatalogSpecification = /^(?:[-•]\s*)?(?:lungime(?:a)? (?:de debitare|de sectionare|profil|cablu)|interval de (?:debitare|sectionare)|culoare (?:furtun|suport)|material (?:carcasa|led)|sursa lumina|latime banda pcb|tensiune de intrare|dimensiune|dimensiuni (?:profil|plasa|baza|standard)|numar siraguri|distanta dintre (?:becuri|siraguri)|forma bec|durata medi[ae] de viata|durata de viata (?:a )?bateriilor|durata de viata baterii|temperatura (?:luminii|de functionare)|umiditate de functionare|diametrul cablu pvc|lumina led-uri|putere (?:maxima|de intrare|totala ghirlanda luminoasa [\d.,]+x[\d.,]+ m)|ecran tactil|rezolutie ecran|parametrii electrici|protocol|wifi|bluetooth|aplicatie|conexiune wireless|functioneaza cu|programe|transformator|telecomanda|utilizare|pozitionare)\s*:/i;
const ltCopyUnpunctuatedSpecification = /^\s*(?:[-•]\s*)?(?:CRI(?:\s+Ra)?|Grosime)\s*(?=[<>≥≤=]?\s*\d)/i;
const ltCopyIsSpecification = text => ltCopySpecification.test(text) || ltCopyAdditionalSpecification.test(text)
  || ltCopyCatalogSpecification.test(text) || ltCopyUnpunctuatedSpecification.test(text);
class LuminiTerasaProductCopy extends HTMLElement {
  connectedCallback() {
    if (this.dataset.enhanced) return;
    this.dataset.enhanced = 'true';
    if (this.dataset.ltStructured === 'true') {
      this.enhanceAdministrativeVideos();
      this.dataset.layoutReady = 'true';
      return;
    }
    const description = this.querySelector('.lt-product-copy__description');
    if (!description) return;
    // The shared showroom block is independently configurable in the theme.
    // During source migration remove only the exact old invitation, not product
    // use cases mentioning showrooms or installation/service information.
    description.querySelectorAll('p,div').forEach(paragraph => {
      if (paragraph.querySelector('p,div,img,iframe,video,table')) return;
      if (/^Te asteptam sa il vezi in Showroom-ul LuminiTerasa!?$/i.test(ltCopyNormalize(paragraph.textContent))) paragraph.remove();
    });
    // Hide only verified technical-sheet images in this description, never gallery media.
    // If a replacement sheet is selected, retain the legacy aliases for this product.
    const imageKey = (src) => {
      try { return new URL(src, location.origin).pathname.split('/').pop().replace(/_\d+x\d+(?=\.)/, ''); }
      catch { return ''; }
    };
    const sheetLinks = [...this.querySelectorAll('.lt-downloads a[data-technical-sheet]')];
    const legacySheets = this.querySelector('.lt-downloads__legacy-sheets');
    if (legacySheets) sheetLinks.push(...legacySheets.content.querySelectorAll('a[data-technical-sheet]'));
    const sheetKeys = new Set(sheetLinks.map(link => imageKey(link.getAttribute('href'))).filter(Boolean));
    description.querySelectorAll('img').forEach((image) => {
      if (!sheetKeys.has(imageKey(image.getAttribute('src')))) return;
      let wrapper = image.parentElement;
      image.remove();
      while (wrapper && wrapper !== description && !wrapper.textContent.trim()
        && !wrapper.querySelector('img,iframe,video,table')) {
        const parent = wrapper.parentElement;
        wrapper.hidden = true;
        wrapper = parent;
      }
    });
    // Collapse only legacy download-only paragraphs, never technical instructions.
    // Product descriptions in Shopify are not modified.
    if (this.querySelector('.lt-downloads')) {
      description.querySelectorAll('p').forEach((paragraph) => {
        const links = [...paragraph.querySelectorAll('a[href]')];
        const isPdf = (link) => /\.pdf(?:[?#]|$)/i.test(link.getAttribute('href'));
        const text = paragraph.textContent.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (links.some(isPdf) && links.every(isPdf) && /^Descarca(?:ti)?\s+de\s+aici\b/i.test(text)
          && text.length < 240 && !paragraph.querySelector('img,iframe,video,table')) {
          paragraph.hidden = true;
        }
      });
    }
    description.querySelectorAll('iframe').forEach((frame) => {
      const width = Number(frame.getAttribute('width'));
      const height = Number(frame.getAttribute('height'));
      if (width > 0 && height > 0) {
        frame.style.setProperty('--lt-video-ratio', `${width} / ${height}`);
        frame.toggleAttribute('data-portrait', height > width);
      }
      frame.setAttribute('loading', 'lazy');
      if (!frame.title) frame.title = 'Prezentare video a produsului';
    });
    description.querySelectorAll('table').forEach((table) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'lt-product-copy__table-scroll';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', 'Tabel cu specificații; derulează orizontal dacă este necesar');
      table.before(wrapper);
      wrapper.append(table);
    });
    description.querySelectorAll('p').forEach((paragraph) => {
      if (!paragraph.textContent.trim() && !paragraph.querySelector('img, iframe, video, a, table')) paragraph.hidden = true;
    });
    if (this.organizeExplicitDescription(description)) {
      this.enhanceAdministrativeVideos();
      this.dataset.layoutReady = 'true';
      return;
    }
    const videos = this.extractVideos(description);
    this.organize(description, videos);
    this.dataset.layoutReady = 'true';
  }

  organizeExplicitDescription(description) {
    // Standard editable H2 headings, not hidden markers or duplicated metafields.
    // Require a clear overview heading and another known section; legacy content
    // otherwise follows the unchanged compatibility parser below.
    const labels = {
      'despre produs': 'overview',
      'specificatii tehnice': 'specs',
      'montaj si compatibilitate': 'installation',
      'video de prezentare': 'media',
    };
    const keyFor = node => node?.nodeType === 1 && node.matches('h2')
      ? labels[ltCopyNormalize(node.textContent).toLowerCase().replace(/\s*:\s*$/, '')] : undefined;
    const nodes = [...description.childNodes];
    const headings = nodes.filter(node => keyFor(node));
    if (keyFor(headings[0]) !== 'overview' || new Set(headings.map(keyFor)).size < 2) return false;
    const groups = {overview: [], specs: [], installation: [], media: []};
    let section = 'overview';
    for (const node of nodes) {
      const key = keyFor(node);
      if (key) {section = key; node.remove(); continue;}
      // Keep every node, including unrecognized headings, lists and rich links.
      if (section === 'specs' && node.nodeType === 1 && node.matches('p,div')
        && !node.querySelector('p,div,ul,ol,img,iframe,video,table,br')
        && (node.textContent.includes(':') || ltCopyUnpunctuatedSpecification.test(node.textContent))) {
        node.classList.add('lt-product-copy__spec-row');
        this.formatSpecification(node);
      }
      if (section === 'media' && node.nodeType === 1 && node.matches('p')
        && node.querySelectorAll('a[href]').length === 1 && !node.querySelector('iframe,video,img')) {
        node.setAttribute('data-lt-admin-video', '');
      }
      groups[section].push(node);
    }
    this.renderSections(description, groups);
    this.dataset.descriptionSource = 'headings';
    return true;
  }

  enhanceAdministrativeVideos() {
    // A readable link remains available without JavaScript and for unknown hosts.
    // Third-party players load only after the visitor opens the video section.
    const section = this.querySelector('.lt-product-copy__section--media');
    if (!section) return;
    const load = () => {
      if (!section.open) return;
      section.querySelectorAll('[data-lt-admin-video]').forEach(item => {
        if (item.dataset.loaded) return;
        const link = item.querySelector('a[href]');
        if (!link) return;
        let url;
        try { url = new URL(link.href); } catch { return; }
        if (url.protocol !== 'https:') return;
        let embed = '';
        const host = url.hostname.toLowerCase();
        if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com', 'youtu.be'].includes(host)) {
          const id = host === 'youtu.be' ? url.pathname.split('/')[1]
            : url.searchParams.get('v') || url.pathname.match(/^\/(?:embed|shorts)\/([^/]+)/)?.[1];
          if (/^[\w-]{11}$/.test(id || '')) embed = `https://www.youtube-nocookie.com/embed/${id}`;
        } else if (['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'].includes(host)) {
          const id = url.pathname.match(/^\/(?:video\/)?(\d+)(?:\/|$)/)?.[1];
          const privateHash = url.searchParams.get('h') || url.pathname.match(/^\/\d+\/([a-zA-Z0-9]+)$/)?.[1];
          if (id) embed = `https://player.vimeo.com/video/${id}${privateHash ? `?h=${encodeURIComponent(privateHash)}` : ''}`;
        }
        if (embed) {
          const frame = document.createElement('iframe');
          frame.src = embed;
          frame.title = 'Prezentare video a produsului';
          frame.loading = 'lazy';
          frame.allowFullscreen = true;
          frame.allow = 'fullscreen; picture-in-picture';
          item.prepend(frame);
          item.dataset.loaded = 'true';
        } else if (host === 'cdn.shopify.com' && /\.mp4$/i.test(url.pathname)) {
          const video = document.createElement('video');
          video.src = url.href;
          video.controls = true;
          video.playsInline = true;
          video.preload = 'none';
          item.prepend(video);
          item.dataset.loaded = 'true';
        }
      });
    };
    section.addEventListener('toggle', load);
    load();
  }

  extractVideos(description) {
    const videos = [];
    description.querySelectorAll('iframe,video').forEach(media => {
      if (media.matches('iframe')) {
        let host;
        try { host = new URL(media.getAttribute('src'), location.origin).hostname; } catch { return; }
        if (!/(^|\.)(youtube\.com|youtube-nocookie\.com|vimeo\.com)$/.test(host)) return;
      }
      // Move the actual media node, not its mixed-content paragraph. This keeps
      // all text, links and captions even when the embed is nested in a span.
      let parent = media.parentElement;
      const item = document.createElement('div');
      item.className = 'lt-product-copy__video';
      item.append(media);videos.push(item);
      while (parent && parent !== description && !parent.textContent.trim()
        && !parent.querySelector('img,iframe,video,a,table,input,button')) {
        const next = parent.parentElement;parent.remove();parent = next;
      }
    });
    return videos;
  }

  organize(description, videos = []) {
    // Reorganize existing DOM only: keep every instruction, link and media item.
    // Ambiguous mixed-content blocks remain intact in the description.
    this.prepareSpecificationBlocks(description);
    const children = [...description.children];
    const groups = { overview: [], specs: [], installation: [], media: videos };
    const normalize = ltCopyNormalize;
    for (const node of children) {
      if (node.hidden) continue;
      const text = normalize(node.textContent);
      if (node.matches('p,div') && ltCopyIsSpecification(text) && !node.querySelector('p,div,ul,ol,img,iframe,video,table')) {
        node.classList.add('lt-product-copy__spec-row');
        this.formatSpecification(node);
        groups.specs.push(node); continue;
      }
      if (/^(specificatii(?: tehnice)?|caracteristici(?: tehnice)?)\s*:?$/i.test(text)) {
        // Keep the source heading in its destination, with no duplicate visual title.
        node.classList.add('lt-product-copy__source-heading');
        groups.specs.push(node); continue;
      }
      if (node.matches('p,ul,ol') && /\b(compatibil|driver|alimentar|montaj|instal|conect|dimmer|gateway|control)/i.test(text)
        && !node.querySelector('iframe,video,img,table')) { groups.installation.push(node); continue; }
      groups.overview.push(node);
    }
    if (Object.values(groups).filter(nodes => nodes.length).length < 2 && !videos.length && !groups.specs.length) return;
    this.renderSections(description, groups);
  }

  renderSections(description, groups) {
    const container = document.createElement('div');
    container.className = 'lt-product-copy__sections';
    const sections = [
      ['overview', 'Despre produs', true],
      ['specs', 'Specificații tehnice', true],
      ['installation', 'Montaj și compatibilitate', true],
      ['media', 'Video de prezentare', false],
    ];
    for (const [key, label, initiallyOpen] of sections) {
      const nodes = groups[key]; if (!nodes.length) continue;
      const details = document.createElement('details');
      details.className = `lt-product-copy__section lt-product-copy__section--${key}`;
      details.open = initiallyOpen;
      const summary = document.createElement('summary'); summary.textContent = label;
      const body = document.createElement('div'); body.className = 'lt-product-copy__section-body';
      nodes.forEach(node => body.append(node));
      details.append(summary, body);container.append(details);
    }
    description.append(container);
    const downloads = this.querySelector('.lt-downloads');
    if (downloads) this.append(downloads);
  }

  prepareSpecificationBlocks(description) {
    // Some legacy paragraphs contain a technical photo immediately before a row.
    // Keep the photo (and an image-only link) as a separate block, then move the text.
    for (const node of [...description.querySelectorAll('p,li,div')]) {
      if(node.closest('table')||node.querySelector('p,li,div,table,iframe,video')||!ltCopyIsSpecification(ltCopyNormalize(node.textContent))) continue;
      for(const image of [...node.querySelectorAll('img')]) {
        const anchor=image.closest('a');if(anchor&&anchor.textContent.trim())continue;
        const photo=document.createElement('p');photo.append(anchor||image);node.before(photo);
      }
    }
    // Older descriptions mix prose and specifications with <br>, <div> or lists.
    // Split only blocks with an explicitly recognized specification, preserving inline markup.
    for (const node of [...description.querySelectorAll('p,li,div')]) {
      if (node.closest('table') || node.querySelector('p,li,div,table')) continue;
      const breaks=[...node.querySelectorAll('br')]; if(!breaks.length) continue;
      const pieces=[]; let previous=null;
      for(const end of [...breaks,null]) {
        const range=document.createRange();
        if(previous) range.setStartAfter(previous); else range.setStart(node,0);
        if(end) range.setEndBefore(end); else range.setEnd(node,node.childNodes.length);
        const part=document.createElement(node.matches('li')?'li':'p');part.append(range.cloneContents());
        if(part.textContent.trim()||part.querySelector('img,a,iframe,video')) pieces.push(part);
        previous=end;
      }
      if(pieces.some(p=>ltCopyIsSpecification(ltCopyNormalize(p.textContent)))) node.replaceWith(...pieces);
    }
    // A few source descriptions put a field label and its value in separate paragraphs.
    // Join only an empty recognized field or an explicitly unfinished parenthesis.
    for (const node of [...description.querySelectorAll('p,li,div')]) {
      if (node.closest('table') || node.querySelector('p,li,div,table')) continue;
      const text = ltCopyNormalize(node.textContent);
      if (!ltCopyIsSpecification(text)) continue;
      const emptyField = /:\s*$/.test(text);
      const openParenthesis = (text.match(/\(/g)||[]).length > (text.match(/\)/g)||[]).length;
      if (!emptyField && !openParenthesis) continue;
      let next = node.nextElementSibling;
      while (next && (next.matches('meta') || (!next.textContent.trim() && !next.querySelector('img,iframe,video,a,table')))) next = next.nextElementSibling;
      if (!next || !next.matches('p,div') || next.querySelector('p,div,ul,ol,img,iframe,video,table')) continue;
      const value = ltCopyNormalize(next.textContent);
      if (!value || ltCopyIsSpecification(value) || /^(specificatii|caracteristici|kitul|pachetul|te asteptam)/i.test(value)) continue;
      if (!emptyField && !/^[a-z]/.test(value)) continue;
      node.append(document.createTextNode(' '), ...next.childNodes); next.remove();
    }
    // Lift individual specification rows out of lists/wrappers, not the entire prose list.
    for(const node of [...description.querySelectorAll('p,li,div')]) {
      if(node.parentElement===description||node.closest('table')||node.querySelector('p,li,div,ul,ol,img,iframe,video,table')) continue;
      const text=ltCopyNormalize(node.textContent);
      if(!ltCopyIsSpecification(text)&&!/^(specificatii(?: tehnice)?|caracteristici(?: tehnice)?)\s*:?$/i.test(text)) continue;
      let top=node;while(top.parentElement&&top.parentElement!==description)top=top.parentElement;
      const row=document.createElement('p');row.append(...node.childNodes);top.before(row);
      let parent=node.parentElement;node.remove();
      while(parent&&parent!==description&&!parent.textContent.trim()&&!parent.querySelector('img,a,iframe,video,table')) {
        const next=parent.parentElement;parent.remove();parent=next;
      }
    }
  }

  formatSpecification(node) {
    // Split at the existing delimiter (or the numeric CRI/thickness boundary),
    // even across nested text nodes. Preserve all original text and inline markup.
    const raw = node.textContent;
    const plain = raw.match(ltCopyUnpunctuatedSpecification);
    let remaining = plain ? plain[0].length : raw.indexOf(':') + 1;
    if (!remaining) return;
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    let text;
    while ((text = walker.nextNode())) {
      if (remaining > text.textContent.length) { remaining -= text.textContent.length; continue; }
      const range = document.createRange();
      range.setStart(node, 0);range.setEnd(text, remaining);
      const label = document.createElement('span');label.className='lt-product-copy__spec-label';
      label.append(range.extractContents());
      const value = document.createElement('span');value.className='lt-product-copy__spec-value';
      value.append(...node.childNodes);
      node.append(label,value);
      break;
    }
  }
}
if (!customElements.get('lt-product-copy')) customElements.define('lt-product-copy', LuminiTerasaProductCopy);
